import { Box, Button, Text, useDisclosure } from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { VscRefresh } from "react-icons/vsc";
import { useDispatch } from "react-redux";
import { getDocumentByUUID } from "../../../../api/documentsService";
import useCustomToast from "../../../../customHooks/useCustomToast";
import { setDocumentsFromApiCall } from "../../../../Redux/documentsSlice";
import {
  cancelUpload,
  initiateCancelUploadProcess,
  keepUploading,
} from "../../../../utils/cancelFileUpload";
import { uploadFileWithProgress } from "../../../../utils/UTILITY_METHODS";
import FileUpload from "../../../FileUpload";
import CancelFileUploadModal from "../../../Modals/CancelFileUploadModal";
import DeleteFileConfirmModal from "../../../Modals/DeleteFileConfirmModal";
import UploadProgress from "../../../UploadProgress";

const DocumentUpload = ({
  cardDetails,
  documentType,
  documentSubType,
  documentNumber,
  documentIdentifier,
  thumbnail,
  width,
}) => {
  const [uploadSuccess, setUploadSuccess] = useState(!!cardDetails);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [lastFile, setLastFile] = useState(null);
  const dispatch = useDispatch();
  const showToast = useCustomToast();
  const [deleteFileLoader, setDeleteFileLoader] = useState(false);
  // Create a ref to store the AbortController
  const abortControllerRef = useRef(null);

  // Modal controls for cancel confirmation
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();

  // useDisclosure for Delete Confirmation Modal
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();

  // Create a ref for the hidden file input
  const fileInputRef = useRef(null);

  const handleFileUpload = async (files) => {
    const file = files[0];
    setLastFile(file);
    setUploadFailed(false);
    setIsUploading(true);

    // Initialize a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = await uploadFileWithProgress(
        file,
        setUploadProgress,
        setUploadFailed,
        setUploadSuccess,
        {
          documentType: documentType,
          documentSubType,
          documentNumber,
          documentNumberIdentifier: documentIdentifier,
          level: "user",
        },
        abortController.signal // Pass the abort signal to handle cancellation
      );
      setIsUploading(false);
      showToast(data.message, "success");
      const documentUUID = data.data;
      const documentDetails = await getDocumentByUUID(documentUUID);
      dispatch(setDocumentsFromApiCall([documentDetails.data]));
      abortControllerRef.current = null; // Reset controller
    } catch (error) {
      if (error.message === "Upload canceled") {
        showToast("Upload canceled", "warning");
      } else {
        showToast(error.message || "An error occurred during upload", "error");
      }
      setIsUploading(false);
      abortControllerRef.current = null; // Reset controller
    }
  };

  // Handle clicking the "Replace File" button by triggering the file input
  const handleReplaceClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle selecting a new file for replacement
  const handleReplaceFileChange = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      const fileType = files[0].type;

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];

      // Check if the selected file is an allowed type
      if (!allowedTypes.includes(fileType)) {
        showToast(
          "Unsupported file format. Please upload the correct file type.",
          "error"
        );
        return;
      } else {
        handleFileUpload(files); // Reuse existing upload logic
      }
    }
  };

  const retryUploadProcess = () => {
    if (uploadFailed && lastFile) {
      handleFileUpload([lastFile]);
    }
  };

  return (
    <Box width={width}>
      <FileUpload
        fileName={documentSubType}
        width={"100%"}
        onFileUpload={handleFileUpload}
        isUploading={isUploading}
        uploadFailed={uploadFailed}
        uploadSuccess={uploadSuccess}
        thumbnail={thumbnail}
        isIdentificationDocument={true}
        acceptImageOrPdf={true}
        originalFileName={cardDetails?.original_filename}
      />

      {uploadSuccess && (
        <Box
          display={"flex"}
          justifyContent={"space-around"}
          paddingX={"10px"}
          mt={2}
        >
          <Button
            size="xs"
            colorScheme="teal"
            variant="unstyled"
            color={"teal.500"}
            display={"flex"}
            columnGap={"1"}
            fontWeight={"semibold"}
            onClick={handleReplaceClick} // Trigger file input
            isDisabled={isUploading} // Disable during upload
          >
            <VscRefresh /> Replace File
          </Button>
          <Button
            size="xs"
            colorScheme="orange"
            variant="unstyled"
            color={"red.500"}
            display={"flex"}
            columnGap={"1"}
            fontWeight={"semibold"}
            isDisabled={isUploading} // Disable during upload
            onClick={openDeleteModal}
          >
            <RiDeleteBin6Line /> Delete
            <DeleteFileConfirmModal
              isOpen={isDeleteModalOpen}
              onClose={closeDeleteModal}
              deleteFileLoader={deleteFileLoader}
              setDeleteFileLoader={() => {
                setDeleteFileLoader(true);
                setTimeout(() => {
                  setDeleteFileLoader(false);
                  closeDeleteModal();
                }, 5000);
              }}
            />
          </Button>
        </Box>
      )}

      <Text
        fontSize={"sm"}
        textAlign={"center"}
        color={"gray.600"}
        fontWeight={"medium"}
        mt={2}
      >
        {!uploadSuccess && "Upload"} {documentIdentifier}
      </Text>

      {/* Hidden file input for replacing the file */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleReplaceFileChange}
      />

      {/* Upload progress */}
      {isUploading && (
        <UploadProgress
          documentType={documentIdentifier}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          uploadFailed={uploadFailed}
          initiateCancelUploadProcess={() =>
            initiateCancelUploadProcess(abortControllerRef, openModal)
          }
          onRetry={retryUploadProcess}
        />
      )}

      {/* Cancel Upload Confirmation Modal */}
      <CancelFileUploadModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        cancelUpload={() =>
          cancelUpload(
            abortControllerRef,
            setIsUploading,
            setUploadProgress,
            closeModal
          )
        }
        keepUploading={() => keepUploading(closeModal)}
      />
    </Box>
  );
};

export default DocumentUpload;
